import { TarotCardData } from '../types/tarot';
import { neynarService } from '../services/neynarService';
import { ShareResult } from '../types/farcaster';
import { generateReferralUrl } from './referrals';
import { sdk } from '@farcaster/miniapp-sdk';

export const FARCASTER_DEEP_LINK = 'https://farcaster.xyz/miniapps/sJ0KyNDz0aTw/stradamus';

export const formatTarotReading = (cards: TarotCardData[]): string => {
  const cardNames = cards.map(card => card.name).join(', ');
  return `🔮 My Tarot Reading: ${cardNames}\n\nDiscover your daily guidance at our Tarot app! ✨`;
};

const buildShareText = (cards: TarotCardData[], override?: string): string => {
  if (override) return override;
  return formatTarotReading(cards);
};

export const shareToFarcaster = async (
  cards: TarotCardData[],
  signerUuid: string,
  options?: {
    includeImage?: boolean;
    channelId?: string;
    replyTo?: string;
  }
): Promise<ShareResult> => {
  try {
    const text = formatTarotReading(cards);
    const embeds = options?.includeImage ? [{ url: 'https://your-app.com/og-image.png' }] : undefined;

    const result = await neynarService.publishCast({
      signerUuid,
      text,
      embeds,
      channelId: options?.channelId,
      replyTo: options?.replyTo,
    });

    return {
      success: true,
      castHash: result.cast.hash,
      url: `https://farcaster.xyz/~/conversations/${result.cast.hash}`,
    };
  } catch (error) {
    console.error('Error sharing to Farcaster:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to share to Farcaster',
    };
  }
};

export const generateShareableLink = (cards: TarotCardData[], userFid?: string): string => {
  const cardIds = cards.map(card => card.id);
  const url = new URL(FARCASTER_DEEP_LINK);

  if (userFid) {
    // Use referral URL if user FID is provided
    const referralUrl = generateReferralUrl(userFid, cardIds);
    const referral = new URL(referralUrl);
    referral.searchParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });
    return url.toString();
  }

  if (cardIds.length > 0) {
    url.searchParams.set('cards', cardIds.join(','));
  }

  return url.toString();
};

interface ShareOverrides {
  textOverride?: string;
  shareUrlOverride?: string;
  includeImage?: boolean;
}

export const shareToWarpcast = (
  cards: TarotCardData[],
  userFid?: string,
  options?: ShareOverrides,
): void => {
  const text = buildShareText(cards, options?.textOverride);
  const shareUrl = options?.shareUrlOverride ?? generateShareableLink(cards, userFid);
  const includeImage = options?.includeImage ?? false;
  const firstCardImage = includeImage && cards.length > 0 ? cards[0].image : null;

  const openInBrowser = () => {
    let farcasterUrl = `https://farcaster.xyz/~/compose?text=${encodeURIComponent(text)}&embeds[]=${encodeURIComponent(shareUrl)}`;

    if (firstCardImage) {
      const fullImageUrl = firstCardImage.startsWith('http') ? firstCardImage : `${window.location.origin}${firstCardImage}`;
      farcasterUrl += `&embeds[]=${encodeURIComponent(fullImageUrl)}`;
    }

    window.open(farcasterUrl, '_blank');
  };

  sdk.isInMiniApp().then((isMiniApp) => {
    if (isMiniApp) {
      const embeds: string[] = [shareUrl];
      if (firstCardImage) {
        const fullImageUrl = firstCardImage.startsWith('http') ? firstCardImage : `${window.location.origin}${firstCardImage}`;
        embeds.push(fullImageUrl);
      }

      sdk.actions
        .composeCast({
          text,
          embeds: embeds.slice(0, 2) as [] | [string] | [string, string],
        })
        .catch((error) => {
          console.warn('composeCast failed, falling back to browser:', error);
          openInBrowser();
        });
      return;
    }

    openInBrowser();
  });
};

export const shareToTwitter = (
  cards: TarotCardData[],
  userFid?: string,
  options?: ShareOverrides,
): void => {
  const text = buildShareText(cards, options?.textOverride);
  const shareUrl = options?.shareUrlOverride ?? generateShareableLink(cards, userFid);
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;

  sdk.isInMiniApp().then((isMiniApp) => {
    if (isMiniApp) {
      sdk.actions
        .openUrl(twitterUrl)
        .catch((error) => {
          console.warn('openUrl failed, falling back to browser:', error);
          window.open(twitterUrl, '_blank');
        });
      return;
    }

    window.open(twitterUrl, '_blank');
  });
};

export const copyToClipboard = async (
  cards: TarotCardData[],
  options?: ShareOverrides,
): Promise<boolean> => {
  try {
    const text = buildShareText(cards, options?.textOverride);
    const shareUrl = options?.shareUrlOverride ?? generateShareableLink(cards);
    const fullText = `${text}\n\n${shareUrl}`;
    
    await navigator.clipboard.writeText(fullText);
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};
